import { GenericApiResponse } from "src/@types/apis";
import { IDimensionValue } from "src/@types/dimension";
import {
  DeleteAsync,
  GetAsync,
  PostAsync,
  PutAsync,
} from "src/common/httpRequests";
import { PROCUREMENT_SERVICE_URL } from "src/constants/api";
import { API_GATEWAY } from "src/constants/settings";
import { handleResponse } from "src/helpers/helpers";
import { getDimensionList } from "src/services/dimensionServices";
import { getDimensionValuesByDimensionId } from "src/services/dimensionValuesServices";
import {
  IPurchaseRequisitionListParams,
  IPurchaserRequisitionById,
  PurchaseRequisitionResponse,
} from "../types/purchaseRequisitionTypes";

const DIMENSION_VALUES_PAGE_SIZE = 100;
const REQUISITION_DIMENSION_NAMES = ["Departments", "CostCenters"];

const getRequisitionDimensionValues = async (): Promise<
  GenericApiResponse<Record<string, IDimensionValue[]>>
> => {
  const dimensionListRes = await getDimensionList();
  const dimensions = (dimensionListRes.data?.dimensions ?? []).filter((d) =>
    REQUISITION_DIMENSION_NAMES.includes(d.name),
  );

  const result: Record<string, IDimensionValue[]> = {};

  for (const dimension of dimensions) {
    const valuesRes = await getDimensionValuesByDimensionId({
      dimensionId: dimension.id,
      page: 1,
      record: DIMENSION_VALUES_PAGE_SIZE,
    });

    result[dimension.name] = valuesRes.data?.dimensionValues ?? [];
  }

  return { data: result, error: null, forbidden: false };
};

export const PROCUREMENT_ENDPOINT = `${API_GATEWAY}${PROCUREMENT_SERVICE_URL}/PurchaseRequisition`;

const getPurchaseRequisitions = async (
  params: IPurchaseRequisitionListParams,
): Promise<GenericApiResponse<PurchaseRequisitionResponse>> => {
  const res = await GetAsync(
    `${PROCUREMENT_ENDPOINT}/ListPurchaseRequisitions`,
    true,
    null,
    { params },
  );

  return handleResponse(res);
};

const createPurchaseRequisition = async (formData: FormData) => {
  const res = await PostAsync(
    `${PROCUREMENT_ENDPOINT}/CreatePurchaseOrder`,
    formData,
  );

  return handleResponse(res);
};

const updatePurchaseRequisition = async (formData: FormData) => {
  const res = await PostAsync(
    `${PROCUREMENT_ENDPOINT}/UpdatePurchaseOrder`,
    formData,
  );

  return handleResponse(res);
};

const getPurchaseRequisitionById = async (
  documentId: string,
): Promise<GenericApiResponse<IPurchaserRequisitionById>> => {
  const res = await GetAsync(
    `${PROCUREMENT_ENDPOINT}/GetPurchaseRequisitionById?documentId=${documentId}`,
  );
  return handleResponse(res);
};

const deletePurchaseRequisition = async (
  documentId: string,
): Promise<GenericApiResponse<undefined>> => {
  const res = await DeleteAsync(
    `${PROCUREMENT_ENDPOINT}/deletePurchaseRequisition?documentId=${documentId}`,
  );

  return handleResponse(res);
};

const postPurchaseRequisition = async (
  id: string,
): Promise<GenericApiResponse<undefined>> => {
  const res = await PutAsync(`${PROCUREMENT_ENDPOINT}/${id}/post`, undefined);

  return handleResponse(res);
};

const discardPurchaseRequisition = async (
  id: string,
  discardReason: string,
): Promise<GenericApiResponse<undefined>> => {
  const res = await PutAsync(`${PROCUREMENT_ENDPOINT}/${id}/discard`, {
    discardReason,
  });

  return handleResponse(res);
};

export {
  createPurchaseRequisition,
  deletePurchaseRequisition,
  discardPurchaseRequisition,
  getPurchaseRequisitionById,
  getPurchaseRequisitions,
  getRequisitionDimensionValues,
  postPurchaseRequisition,
  updatePurchaseRequisition,
};
